import { useCTW } from '@ctw/shared/context/ctw-context';
import { useFeatureFlag } from '@ctw/shared/context/feature-flag-provider';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import {
  AI_API_VERSION_V1,
  AI_API_VERSION_V2,
  getAiServiceBaseUrl,
} from '@ctw/shared/utils/environments';
import { useCallback, useEffect, useMemo, useState } from 'react';

export type SummaryType = string;

export type SummaryConfigs = {
  EntityId: string;
  DefaultSummarySlug: SummaryType;
  SummaryConfigs: Record<string, SummaryConfig>;
};

export type SummaryConfig = {
  SummarySlug: string;
  SummaryName: string;
  Timeframe: string;
  Checklist: string;
};

interface UseConfigsResult {
  configs: SummaryConfigs | null;
  loading: boolean;
  error: Error | null;
  updateConfigs: (newConfigs: SummaryConfigs) => Promise<void>;
  fetchConfigs: () => Promise<void>;
}

/**
 * Custom hook to fetch and update all summary configurations for a builder ID.
 * @param {string} builderId The builder ID.
 * @returns {UseConfigsResult} An object containing the configs, loading state, error state, and an update function.
 */
function useSummaryConfig(builderId: string): UseConfigsResult {
  const [configs, setConfigs] = useState<SummaryConfigs | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const telemetry = useTelemetry();
  const { requestContext, ctwFetch } = useCTW();
  const aiApiVersion = useFeatureFlag('ctw-python-summarization')
    ? AI_API_VERSION_V2
    : AI_API_VERSION_V1;

  const apiUrl = useMemo(() => {
    if (!builderId) {
      return null;
    }
    return `${getAiServiceBaseUrl({ env: requestContext.env, version: aiApiVersion })}/config/${builderId}`;
  }, [builderId, requestContext.env, aiApiVersion]);

  const fetchConfigs = useCallback(async () => {
    if (!apiUrl) {
      setConfigs(null);
      setLoading(false);
      setError(new Error('Builder ID is required.'));
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await ctwFetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${requestContext.authToken}`,
          'Zus-Account': requestContext.builderId,
        },
      });

      if (!response.ok) {
        throw new Error('HTTP error fetching configs!');
      }

      const data: SummaryConfigs = response.data as SummaryConfigs;
      setConfigs(data);
    } catch (err) {
      const error =
        err instanceof Error ? err : new Error(`An unknown error occurred during fetch: ${err}`);
      telemetry.trackError({
        message: 'Error fetching configs',
        error: err,
      });
      setError(error);
      setConfigs(null);
    } finally {
      setLoading(false);
    }
  }, [apiUrl, ctwFetch, requestContext.authToken, requestContext.builderId, telemetry]);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  const updateConfigs = useCallback(
    async (newConfigs: SummaryConfigs) => {
      if (!apiUrl) {
        setError(new Error('Builder ID is required to update.'));
        return;
      }

      setLoading(true);
      setError(null);
      try {
        // TODO: fix backend API to return a JSON response to match PUT behavior, then
        // use ctwFetch instead of fetch
        const response = await fetch(apiUrl, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${requestContext.authToken}`,
            'Zus-Account': requestContext.builderId,
          },
          body: JSON.stringify(newConfigs),
        });

        if (!response.ok) {
          const data = await response.text();
          const err = new Error(`HTTP error updating configs. Content: ${data}`);
          telemetry.trackError({
            message: 'HTTP error updating configs',
            error: err,
            context: {
              response: data,
            },
          });
          throw err;
        }

        // TODO: fix backend API to return a JSON response to match PUT behavior
        setConfigs(newConfigs);
      } catch (err) {
        const error =
          err instanceof Error ? err : new Error('An unknown error occurred during update');
        telemetry.trackError({
          message: 'HTTP error updating configs',
          error: err,
        });
        setError(error);
      } finally {
        setLoading(false);
      }
    },
    [apiUrl, requestContext.authToken, requestContext.builderId, telemetry],
  );

  return { configs, loading, error, updateConfigs, fetchConfigs };
}

export default useSummaryConfig;
